// FRONT-254: the server's "a late result is about to be spoken into your
// GPT-Live call" signal (`voice-live-late-result`, published on the person's
// own channel by `src/voice/live-jobs.ts`), relayed from the app-level SSE
// stream to the voice call that owns that live session.
//
// The voice call needs it for two things:
//   - the result is already a chat row, so the call's spoken reading of it
//     must not be saved a second time (`LiveCallTracker.lateResultIncoming`);
//   - `endCall`: a hang-up the resolver was asked for after the browser's
//     own answer had already gone out reaches the browser only this way.
//
// A tiny module-level relay rather than the event store: that store is an
// audit ring buffer, not a channel anything acts on (see `event-store.ts`).

export interface LiveLateResultSignal {
  liveSessionId: string;
  endCall: boolean;
}

type Listener = (signal: LiveLateResultSignal) => void;

const listeners = new Set<Listener>();

/** Validate an SSE payload; anything malformed is dropped, never acted on. */
export function parseLiveLateResultSignal(payload: unknown): LiveLateResultSignal | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  const liveSessionId = typeof p.liveSessionId === 'string' ? p.liveSessionId.trim() : '';
  if (!liveSessionId) return null;
  return { liveSessionId, endCall: p.endCall === true };
}

export function publishLiveLateResult(signal: LiveLateResultSignal): void {
  for (const listener of listeners) {
    try {
      listener(signal);
    } catch (err) {
      console.warn('[voice-live] late-result listener threw:', err);
    }
  }
}

/** Returns the unsubscribe function. */
export function subscribeLiveLateResult(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * What the call does with a signal: only the call that owns that live
 * session and is still running acts on it, and only a hands-free call hangs
 * up by voice (the standard call never offers the hang-up at all).
 */
export function lateResultSignalAction(
  signal: LiveLateResultSignal,
  call: { liveSessionId: string | null; stopped: boolean; handsFree: boolean },
): { markReading: boolean; hangUp: boolean } {
  const ours = call.liveSessionId !== null && !call.stopped && call.liveSessionId === signal.liveSessionId;
  return { markReading: ours, hangUp: ours && signal.endCall && call.handsFree };
}
